/* Datei: src/pages/BlogAdmin/KeywordManagement.js */
import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../config/config";
import { useNavigate } from "react-router-dom";

/**
 * Liest aus einem Datenbankdatum (z. B. "2025-02-27T23:00:00.000Z") 
 * ausschließlich den Teil "YYYY-MM-DD" heraus und ignoriert die Uhrzeit.
 * Dadurch umgehen wir Zeitzonenverschiebungen.
 */
const parseDbDateToIso = (dbDate) => {
  if (!dbDate) return "";
  // Beispiel: dbDate = "2025-02-27T23:00:00.000Z"
  // Wir suchen nur den Teil bis zum ersten "T", also "2025-02-27"
  // Regex-Variante:
  const match = dbDate.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

/**
 * Zeigt ein ISO-Datum (YYYY-MM-DD) als deutsches Format (TT.MM.JJJJ) an.
 */
const formatIsoToGerman = (isoStr) => {
  if (!isoStr) return "";
  const [year, month, day] = isoStr.split("-");
  return `${day}.${month}.${year}`;
};

/**
 * Prüft, ob der heutige Tag im Bereich [dateFrom, dateTo] liegt.
 * Beide Parameter sind ISO-Strings ("YYYY-MM-DD").
 */
const isActiveBasedOnDate = (dateFrom, dateTo) => {
  if (!dateFrom || !dateTo) return false;
  const todayIso = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"
  return todayIso >= dateFrom && todayIso <= dateTo;
};

const KeywordManagement = () => {
  const navigate = useNavigate();
  const [keywords, setKeywords] = useState([]);
  const [editingKeywordId, setEditingKeywordId] = useState(null);
  const [editableKeyword, setEditableKeyword] = useState({});

  // Daten für ein neues Keyword
  const [newKeyword, setNewKeyword] = useState({
    keyword: "",
    date_from: "",
    date_to: "",
    infinite: false,
    active: true,
  });

  // Keywords beim Laden der Seite abrufen
  useEffect(() => {
    fetchKeywords();
  }, []);

  // API-Aufruf zur Keyword-Liste
  const fetchKeywords = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/blog-keywords`, {
        headers: { "ngrok-skip-browser-warning": "true" },
      });

      if (Array.isArray(response.data)) {
        // Jedes Keyword in ein einheitliches Format überführen
        const prepared = response.data.map((k) => {
          const isoFrom = parseDbDateToIso(k.date_from);
          const isoTo = parseDbDateToIso(k.date_to);
          return {
            id: k.id,
            keyword: k.keyword || "Unbenannt",
            date_from: isoFrom,    // intern als ISO-String
            date_to: isoTo,        // intern als ISO-String
            active: isActiveBasedOnDate(isoFrom, isoTo),
            infinite: Boolean(k.infinite),
          };
        });
        setKeywords(prepared);
      } else {
        setKeywords([]);
      }
    } catch (error) {
      console.error("Fehler beim Abrufen der Keywords:", error);
      setKeywords([]);
    }
  };

  // Keyword löschen
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${config.API_BASE_URL}/blog-keywords/${id}`);
      fetchKeywords();
    } catch (error) {
      console.error("Fehler beim Löschen:", error);
    }
  };

  // Editiermodus einschalten
  const handleEdit = (id) => {
    const keywordToEdit = keywords.find((k) => k.id === id);
    // Kopie in den State "editableKeyword" legen
    setEditableKeyword({ ...keywordToEdit });
    setEditingKeywordId(id);
  };

  // Bearbeitung abbrechen
  const handleCancelEdit = () => {
    setEditingKeywordId(null);
    setEditableKeyword({});
  };

  // Eingaben in der Bearbeitung speichern
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditableKeyword((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Keyword aktualisieren (PUT)
  const handleUpdate = async (id) => {
    try {
      // editableKeyword enthält bereits date_from und date_to als "YYYY-MM-DD"
      await axios.put(`${config.API_BASE_URL}/blog-keywords/${id}`, {
        active: editableKeyword.active,
        keyword: editableKeyword.keyword,
        date_from: editableKeyword.date_from,
        date_to: editableKeyword.date_to,
        infinite: editableKeyword.infinite,
      });
      setEditingKeywordId(null);
      fetchKeywords();
    } catch (error) {
      console.error("Fehler beim Aktualisieren:", error);
    }
  };

  // Neue Keyword-Eingabe aktualisieren
  const handleNewKeywordChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewKeyword((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Neues Keyword hinzufügen (POST)
  const handleAddNewKeyword = async () => {
    if (!newKeyword.keyword || !newKeyword.date_from || !newKeyword.date_to) {
      alert("Bitte alle Felder ausfüllen!");
      return;
    }

    try {
      // Wir senden die ISO-Daten an den Server
      await axios.post(`${config.API_BASE_URL}/blog-keywords`, {
        keyword: newKeyword.keyword,
        date_from: newKeyword.date_from, // ISO-Format "YYYY-MM-DD"
        date_to: newKeyword.date_to,     // ISO-Format "YYYY-MM-DD"
        infinite: newKeyword.infinite,
        active: newKeyword.active,
      });
      // Eingabefelder zurücksetzen
      setNewKeyword({ keyword: "", date_from: "", date_to: "", infinite: false, active: true });
      fetchKeywords();
    } catch (error) {
      console.error("Fehler beim Hinzufügen:", error);
    }
  };

  return (
    <div className="blog-admin-card-grid">
      <section className="blog-admin-card">
        <div>
          <h2>Keyword Verwaltung</h2>
          <p className="blog-admin-section-copy">
            Laufzeiten, Aktivstatus und Keyword-Fenster bleiben als operativer Inhaltskalender sichtbar.
          </p>
        </div>

        <div className="blog-admin-table-wrap">
          <table className="blog-admin-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Keyword</th>
                <th>Von</th>
                <th>Bis</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {keywords.length > 0 ? (
                keywords.map((k) => (
                  <tr key={k.id}>
                    <td>
                      <span className={`blog-admin-status${k.active ? " is-active" : ""}`}>
                        {k.active ? "Aktiv" : "Inaktiv"}
                      </span>
                    </td>
                    <td>
                      {editingKeywordId === k.id ? (
                        <input
                          type="text"
                          name="keyword"
                          value={editableKeyword.keyword}
                          onChange={handleInputChange}
                          className="blog-admin-input"
                        />
                      ) : (
                        k.keyword
                      )}
                    </td>
                    <td>
                      {editingKeywordId === k.id ? (
                        <input
                          type="date"
                          name="date_from"
                          value={editableKeyword.date_from}
                          onChange={handleInputChange}
                          className="blog-admin-input"
                        />
                      ) : (
                        formatIsoToGerman(k.date_from)
                      )}
                    </td>
                    <td>
                      {editingKeywordId === k.id ? (
                        <input
                          type="date"
                          name="date_to"
                          value={editableKeyword.date_to}
                          onChange={handleInputChange}
                          className="blog-admin-input"
                        />
                      ) : (
                        formatIsoToGerman(k.date_to)
                      )}
                    </td>
                    <td>
                      <div className="blog-admin-keyword-actions">
                        {editingKeywordId === k.id ? (
                          <>
                            <button onClick={() => handleUpdate(k.id)} className="blog-admin-button blog-admin-button--success">
                              Update
                            </button>
                            <button onClick={handleCancelEdit} className="blog-admin-button blog-admin-button--ghost">
                              Abbrechen
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => handleEdit(k.id)} className="blog-admin-button blog-admin-button--warning">
                              Edit
                            </button>
                            <button onClick={() => handleDelete(k.id)} className="blog-admin-button blog-admin-button--danger">
                              Loeschen
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5">
                    <div className="blog-admin-empty">Keine Keywords vorhanden.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="blog-admin-card">
        <div>
          <h3>Neues Keyword setzen</h3>
          <p className="blog-admin-meta">Neue Zeitfenster koennen ohne Fachumbau direkt in dieselbe Admin-Flaeche geschrieben werden.</p>
        </div>
        <div className="blog-admin-form-grid">
          <div className="blog-admin-field">
            <label htmlFor="blog-keyword-new">Keyword</label>
            <input
              id="blog-keyword-new"
              type="text"
              name="keyword"
              placeholder="Neues Keyword"
              value={newKeyword.keyword}
              onChange={handleNewKeywordChange}
              className="blog-admin-input"
              required
            />
          </div>
          <div className="blog-admin-field">
            <label htmlFor="blog-keyword-from">Von</label>
            <input
              id="blog-keyword-from"
              type="date"
              name="date_from"
              value={newKeyword.date_from}
              onChange={handleNewKeywordChange}
              className="blog-admin-input"
              required
            />
          </div>
          <div className="blog-admin-field">
            <label htmlFor="blog-keyword-to">Bis</label>
            <input
              id="blog-keyword-to"
              type="date"
              name="date_to"
              value={newKeyword.date_to}
              onChange={handleNewKeywordChange}
              className="blog-admin-input"
              required
            />
          </div>
        </div>
        <div className="blog-admin-toolbar">
          <button onClick={handleAddNewKeyword} className="blog-admin-button blog-admin-button--primary">
            + Hinzufuegen
          </button>
          <button onClick={() => navigate(-1)} className="blog-admin-button blog-admin-button--ghost">
            Zurueck
          </button>
        </div>
      </section>
    </div>
  );
};

export default KeywordManagement;
